import { useEffect, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Kpi } from "@/components/Kpi";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell,
  AreaChart, Area, Legend,
} from "recharts";
import { Leaf, Tree, Path, Scales, FilePdf, FileCsv } from "@phosphor-icons/react";

export default function Carbon() {
  const [summary, setSummary] = useState(null);
  const [perf, setPerf] = useState(null);

  useEffect(() => {
    api.get("/carbon/summary").then((r) => setSummary(r.data));
    api.get("/performance").then((r) => setPerf(r.data));
  }, []);

  const routes = (perf?.routes || []).slice().sort((a, b) => b.co2 - a.co2).slice(0, 6);
  const vbreak = perf?.vehicle_breakdown || [];
  const VCOLORS = ["#0F172A", "#2563EB", "#059669", "#EAB308", "#DC2626"];

  return (
    <Layout title="ESG & Carbon" subtitle="Per-shipment CO₂ emissions & sustainability performance"
      actions={
        <div className="flex gap-2">
          <Button asChild variant="outline" data-testid="export-csv"><a href={fileUrl("/carbon/export.csv")}><FileCsv size={16} className="mr-1.5" />CSV</a></Button>
          <Button asChild className="bg-emerald-600 hover:bg-emerald-700" data-testid="export-pdf"><a href={fileUrl("/carbon/export.pdf")}><FilePdf size={16} className="mr-1.5" />PDF Report</a></Button>
        </div>
      }>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi testid="esg-total-co2" label="Total Emissions" value={summary?.total_co2_tonnes ?? "—"} unit="t CO₂" icon={Leaf} accent="#059669" />
        <Kpi testid="esg-intensity" label="Avg Intensity" value={summary?.avg_intensity ?? "—"} unit="kg/km" icon={Scales} accent="#2563EB" />
        <Kpi testid="esg-per-ton" label="CO₂ / Ton Cargo" value={summary?.co2_per_ton_cargo ?? "—"} unit="kg" icon={Path} accent="#0F172A" />
        <Kpi testid="esg-trees" label="Trees to Offset" value={summary?.trees_equivalent ?? "—"} unit="/yr" icon={Tree} accent="#16A34A" sub="21.77 kg CO₂/tree" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* emissions per route */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-semibold mb-1">Emissions by Route</h3>
          <p className="text-xs text-slate-400 mb-4">Total CO₂ (kg) per lane · top routes</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={routes} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#E2E8F0" />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <YAxis type="category" dataKey="route" width={120} tick={{ fontSize: 10, fill: "#475569" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="co2" radius={[0, 4, 4, 0]} fill="#059669" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* vehicle mix */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <h3 className="font-heading font-semibold mb-1">Emissions by Vehicle Type</h3>
          <p className="text-xs text-slate-400 mb-4">CO₂ contribution across the fleet mix</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={vbreak}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="type" tick={{ fontSize: 10, fill: "#475569" }} interval={0} angle={-12} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
              <Bar dataKey="co2" radius={[4, 4, 0, 0]}>
                {vbreak.map((_, i) => <Cell key={i} fill={VCOLORS[i % VCOLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* efficiency over time */}
      <div className="bg-white border border-slate-200 rounded-md p-5">
        <h3 className="font-heading font-semibold mb-1">Efficiency Over Time</h3>
        <p className="text-xs text-slate-400 mb-4">Monthly CO₂ vs distance moved</p>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={summary?.trend || []}>
            <defs>
              <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#059669" stopOpacity={0.3} /><stop offset="95%" stopColor="#059669" stopOpacity={0} /></linearGradient>
              <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563EB" stopOpacity={0.25} /><stop offset="95%" stopColor="#2563EB" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94A3B8" }} />
            <YAxis tick={{ fontSize: 11, fill: "#94A3B8" }} />
            <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #E2E8F0" }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Area type="monotone" dataKey="co2" name="CO₂ (kg)" stroke="#059669" fill="url(#g1)" strokeWidth={2} />
            <Area type="monotone" dataKey="distance" name="Distance (km)" stroke="#2563EB" fill="url(#g2)" strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Layout>
  );
}
